import { isEmpty } from '@ember/utils'
import type { NormalizedInterval } from 'date-fns'
import type { SchoolScope, StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type StudentEventsService from 'teacher-ui-client/services/student-events'
import fillMonths from 'teacher-ui-client/utils/student-events/fill-months'

export interface StoriesWrittenTotalsResponse {
  stories_written_totals_summary_by_grade: SummaryData[]
  stories_written_totals_by_grade_by_month: MonthData[]
}

interface SummaryData {
  grade_position: number
  data: [
    { category: 'total_count'; count: number },
    { category: 'student_count'; count: number },
    { category: 'student_average'; average: number },
  ]
}

interface MonthData {
  grade_position: number
  months: {
    month: string
    count: number
  }[]
}

export interface StoriesWrittenTotalsDataItem {
  grade_position: number
  summary: {
    total_count: number
    student_count: number
    student_average: number
  }
  months: {
    month: string
    count: number
  }[]
}

export async function storiesWrittenTotals(
  this: StudentEventsService,
  product: LegacyProductRe,
  statsScope: SchoolScope | StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<StoriesWrittenTotalsDataItem[]>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('stories_written_totals_by_grade_by_month', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<StoriesWrittenTotalsResponse>(url, query)
  if (!data) return null

  const { stories_written_totals_summary_by_grade: summaryData, stories_written_totals_by_grade_by_month: monthData } =
    data

  if (summaryData.length === 0 || monthData.length === 0) return null

  return groupByGrade(summaryData, monthData, period)
}

function groupByGrade(summaryData: SummaryData[], monthData: MonthData[], period: NormalizedInterval) {
  const sortedSummaryData = [...summaryData].sort((a, b) => a.grade_position - b.grade_position)

  return sortedSummaryData
    .map((summary) => {
      const currentMonth = monthData.find((month) => month.grade_position === summary.grade_position)
      if (!currentMonth) return null

      return {
        grade_position: summary.grade_position,
        summary: {
          total_count: summary.data.find((summaryItem) => summaryItem.category === 'total_count')!.count,
          student_count: summary.data.find((summaryItem) => summaryItem.category === 'student_count')!.count,
          student_average: summary.data.find((summaryItem) => summaryItem.category === 'student_average')!.average,
        },
        months: fillMonths(currentMonth.months, period, {
          count: 0,
        }),
      }
    })
    .filter((data) => data !== null)
}
